import React from "react";
import {
  Select,
  NumberInput,
  TextInput,
  Switch,
  Stack,
  Group,
  Text,
} from "@mantine/core";
import { RuntimeFilterDefinition } from "@captiva/shared-types";

export interface RuntimeFilterProps {
  filters?: RuntimeFilterDefinition[];
  values: Record<string, any>;
  onChange: (id: string, value: any) => void;
  optionSets?: Record<string, string[]>;
}

export const DynamicFilterBuilder: React.FC<RuntimeFilterProps> = ({
  filters,
  values,
  onChange,
  optionSets = {},
}) => {
  if (!filters || filters.length === 0) return null;

  return (
    <Stack spacing="sm" mt="sm" mb="md">
      <Text size="sm" weight={600} color="dimmed">
        Report Parameters & Filters
      </Text>
      {filters.map((filter) => {
        const val = values[filter.id] ?? filter.defaultValue;

        // Resolve dropdown options
        let options: string[] = filter.options ? Array.from(new Set(filter.options)) : [];
        if (filter.optionsSource && filter.optionsSource.startsWith("optionSet:")) {
          const setName = filter.optionsSource.replace("optionSet:", "").trim();
          const loaded = optionSets[setName] || optionSets[setName.toLowerCase()] || [];
          const baseOptions = options.filter((o) => o !== "ALL");
          const merged = Array.from(
            new Set([
              ...baseOptions,
              ...loaded.filter((o: string) => Boolean(o) && o !== "ALL"),
            ])
          );
          options = ["ALL", ...merged];
        }

        switch (filter.type) {
          case "dropdown":
            return (
              <Select
                key={filter.id}
                label={filter.label}
                placeholder={filter.placeholder || `Select ${filter.label}`}
                value={val !== undefined && val !== null ? String(val) : "ALL"}
                data={options.map((opt) => ({
                  value: opt,
                  label: opt === "ALL" ? "All (No Filter)" : opt,
                }))}
                onChange={(newVal) => onChange(filter.id, newVal || "ALL")}
                clearable={false}
                searchable
                required={filter.required}
              />
            );

          case "number":
            return (
              <NumberInput
                key={filter.id}
                label={filter.label}
                placeholder={filter.placeholder}
                value={val !== undefined && val !== null && val !== "" ? Number(val) : undefined}
                min={filter.min ?? 0}
                max={filter.max}
                step={filter.step ?? 1}
                onChange={(newVal) => onChange(filter.id, newVal ?? filter.defaultValue)}
                required={filter.required}
              />
            );

          case "text":
            return (
              <TextInput
                key={filter.id}
                label={filter.label}
                placeholder={filter.placeholder}
                value={String(val ?? "")}
                onChange={(e) => onChange(filter.id, e.currentTarget.value)}
                required={filter.required}
              />
            );

          case "toggle":
            return (
              <Group key={filter.id} position="apart" mt="xs">
                <Text size="sm">{filter.label}</Text>
                <Switch
                  aria-label={filter.label}
                  checked={Boolean(val)}
                  onChange={(e) => onChange(filter.id, e.currentTarget.checked)}
                />
              </Group>
            );

          default:
            return null;
        }
      })}
    </Stack>
  );
};

export default DynamicFilterBuilder;
